import { useEffect, useState } from 'react';
import { _TypedDataEncoder } from 'ethers/lib/utils';
import { AlertCircle } from 'lucide-react';
import { numberFromString, toSpecialFixed } from '@/lib/misc/utils';
import { useSwap } from '@/contexts/SwapProvider';
import PreSwapConfirmationModal from './PreSwapConfirmationModal';
import NumberPad from '@/components/trade/NumberPad';
import SelectionScreen from '../mobile/SelectionScreen';
import { Button } from '@/components/ui/button';
import classNames from '@/lib/misc/classNames';
import { validate } from './const';
import { useMixpanel } from '@/lib/misc/mixpanel';
import useLitSession from '@/hooks/LitSessionContext/useLitSession';
export default function SwapTabMobile() {
  const {
    fromAssets,
    setFromAssets,
    toAssets,
    isConfirmationDialogOpen,
    setSwapResult,
    triggerQuote,
    setIsConfirmationDialogOpen,
    setPriceFetching
  } = useSwap();
  const [showAmount, setShowAmount] = useState('0');
  const [isSwitched, setIsSwitched] = useState(false);
  const [tokenAmountToSend, setTokenAmountToSend] = useState('0');
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    PKP
  } = useLitSession();
  useEffect(() => {
    if (!fromAssets[0]) return;
    const _amount = Number(fromAssets[0]?.amountToUseStr)?.toFixed(2);
    if (fromAssets[0]?.quote_rate) {
      const rate = Number(fromAssets[0].quote_rate);
      const tokenAmount = toSpecialFixed(Number(_amount) / rate).value;
      setTokenAmountToSend(tokenAmount);
      setShowAmount(isSwitched ? tokenAmount : _amount);
    } else {
      if (!isSwitched) setShowAmount(_amount);
    }
  }, [isSwitched]);
  const handleNumPadChange = (val: string) => {
    if (!fromAssets[0]) return;
    if (val?.includes('.')) {
      const parts = val.split('.');
      const integerPart = parts[0];
      let decimalPart = parts[1];
      const decimalLength = decimalPart.length;
      if (decimalLength > 3) {
        decimalPart = decimalPart.substring(0, 2);
      }
      val = `${integerPart}.${decimalPart}`;
    }
    const parsedVal = parseFloat(val);
    if (isNaN(parsedVal)) {
      console.error('Invalid numeric input:', val);
      return;
    }
    const rate = Number(fromAssets[0]?.quote_rate);
    if (isNaN(rate)) {
      console.error('Invalid quote rate:', fromAssets[0]?.quote_rate);
      return;
    }
    setShowAmount(val);
    setFromAssets([{
      ...fromAssets[0],
      amountToUseStr: val,
      amountToUse: numberFromString(val)
    }]);
  };
  const handleNumPadTokenChange = (val: string) => {
    if (!fromAssets[0]) return;
    if (!val) {
      val = '0';
    }
    if (val === '') {
      val = '0';
    }
    if (val?.includes('.')) {
      const parts = val.split('.');
      const integerPart = parts[0];
      let decimalPart = parts[1] || '';
      const decimalLength = decimalPart.length;
      if (decimalLength > 4) {
        decimalPart = decimalPart.substring(0, 4);
      }
      val = `${integerPart}.${decimalPart}`;
    }
    const parsedVal = parseFloat(val);
    if (isNaN(parsedVal)) {
      console.error('Invalid numeric input:', val);
      return;
    }
    const rate = Number(fromAssets[0]?.quote_rate);
    if (isNaN(rate)) {
      console.error('Invalid quote rate:', fromAssets[0]?.quote_rate);
      return;
    }
    const tokenAmount = toSpecialFixed(parsedVal * rate).value;
    setShowAmount(val);
    setTokenAmountToSend(val);
    setFromAssets([{
      ...fromAssets[0],
      amountToUseStr: tokenAmount,
      amountToUse: numberFromString(tokenAmount)
    }]);
  };
  function setPercentage(percentage: number) {
    if (!fromAssets[0]) return;
    const _amount = fromAssets[0]?.usd_value * (percentage / 100);
    if (fromAssets[0]?.quote_rate) {
      const rate = Number(fromAssets[0].quote_rate);
      const tokenAmount = toSpecialFixed(Number(_amount) / rate).value;
      setTokenAmountToSend(tokenAmount);
      setShowAmount(isSwitched ? tokenAmount : _amount?.toFixed(2));
    } else {
      if (!isSwitched) setShowAmount(_amount?.toString());
    }
    setFromAssets([{
      ...fromAssets[0],
      amountToUseStr: _amount?.toString(),
      amountToUse: _amount
    }]);
  }
  const sanitizedTotalAmount = showAmount?.replace(/,/g, '') || '0';
  const displayAmount = isSwitched ? sanitizedTotalAmount : sanitizedTotalAmount;
  if (isConfirmationDialogOpen) return <PreSwapConfirmationModal onClickBack={() => setIsConfirmationDialogOpen(!isConfirmationDialogOpen)} />;
  return <div className={`  
        h-[100vh]
        overflow-y-scroll
        pb-[100px]
        w-full
        flex flex-col items-center justify-between        
        ${Number(displayAmount) !== 0 && 'purple-gradient-swap'}  `} data-sentry-component="SwapTabMobile" data-sentry-source-file="SwapTabMobile.tsx">
      <div className='w-full'>
        <SelectionScreen showAmount={showAmount} setShowAmount={setShowAmount} isSwitched={isSwitched} setIsSwitched={setIsSwitched} setPercentage={setPercentage} data-sentry-element="SelectionScreen" data-sentry-source-file="SwapTabMobile.tsx" />
        <div className={`w-full  px-4 flex flex-col text-2xl gap-10 items-center justify-center min-h-[30vh] `}>
          <NumberPad value={fromAssets[0]?.amountToUseStr} onValueChange={handleNumPadChange} handleNumPadTokenChange={handleNumPadTokenChange} tokenAmountToSend={tokenAmountToSend} isSwitched={isSwitched} className={`py-0 ${!fromAssets[0] && 'opacity-50'}`} data-sentry-element="NumberPad" data-sentry-source-file="SwapTabMobile.tsx" />
        </div>
      </div>

      <div className='px-2 w-full'>
        {Boolean(!validate(fromAssets, toAssets).valid && validate(fromAssets, toAssets).error && validate(fromAssets, toAssets)?.error !== 'Swap') && <div className='flex items-center justify-center mb-4 px-4 text-xs'>
            <AlertCircle className='text-yellow-400/70 ' />
            <span className='text-xs text-genius-cream/70'>
              {validate(fromAssets, toAssets).error}
            </span>
          </div>}

        <Button variant='pink' className={classNames('w-full rounded-full h-14')} disabled={!validate(fromAssets, toAssets).valid} onClick={async () => {
        setIsConfirmationDialogOpen(true);
        setPriceFetching(true);
        LogToMixpanel({
          pkp: PKP,
          eventName: EVENTS.trade.startTrade,
          category: CATEGORIES.trade,
          metaData: {
            fromAssets: fromAssets,
            toAssets: toAssets
          }
        });
        setSwapResult({
          error: '',
          time: 0
        });
        await triggerQuote();
        setIsConfirmationDialogOpen(true);
      }} data-sentry-element="Button" data-sentry-source-file="SwapTabMobile.tsx">
          Swap
        </Button>
      </div>
    </div>;
}